import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdvisorDataProtection from './pages/AdvisorDataProtection';
import Dashboard from './pages/Dashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import ClientsList from './pages/ClientsList';
import ClientForm from './pages/ClientForm';
import ClientPlanView from './pages/ClientPlanView';
import Settings from './pages/Settings';
import AddAdvisor from './pages/AddAdvisor';
import TeamReport from './pages/TeamReport';
import MoneySchool from './pages/MoneySchool';
import MoneyMeter from './pages/MoneyMeter';
import PaisoKiPathshala from './pages/PaisoKiPathshala';
import ChildFutureReady from './pages/ChildFutureReady';
import FinancialHealthCheckup from './pages/FinancialHealthCheckup';
import FamilyVault from './pages/FamilyVault';
import ArthKundliPro from './pages/ArthKundliPro';
import GoalManthan from './pages/GoalManthan';
import MSMERiskMRI from './pages/MSMERiskMRI';
import MoneyCompass from './pages/MoneyCompass';
import LeadGenEngine from './pages/LeadGenEngine';
import DebtDetox360 from './pages/DebtDetox360';
import MyViralBrand from './pages/MyViralBrand';
import ArthGyan from './pages/ArthGyan';
import SalesSutra from './pages/SalesSutra';
import WinningAds from './pages/WinningAds';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/advisor-data-protection" element={<AdvisorDataProtection />} />
          
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route 
              path="clients" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager', 'super_admin']}>
                  <ClientsList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="clients/new" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager', 'super_admin']}>
                  <ClientForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="clients/:id/edit" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager', 'super_admin']}>
                  <ClientForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="clients/:id/view" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager', 'super_admin', 'client']}>
                  <ClientPlanView />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="settings" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager', 'super_admin']}>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            {/* Manager-specific routes */}
            <Route 
              path="team/add-advisor" 
              element={
                <ProtectedRoute allowedRoles={['manager', 'super_admin']}>
                  <AddAdvisor />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="team-report" 
              element={
                <ProtectedRoute allowedRoles={['manager', 'super_admin']}>
                  <TeamReport />
                </ProtectedRoute>
              } 
            />
            {/* Money School - Embedded */}
            <Route 
              path="money-school" 
              element={
                <ProtectedRoute allowedRoles={['client', 'advisor', 'manager', 'super_admin']} requiresPro>
                  <MoneySchool />
                </ProtectedRoute>
              } 
            />
            {/* Money Meter - Budget Planner (Client, Advisor, Manager) */}
            <Route 
              path="money-meter" 
              element={
                <ProtectedRoute allowedRoles={['client', 'advisor', 'manager']} requiresPro>
                  <MoneyMeter />
                </ProtectedRoute>
              } 
            />
            {/* Paiso Ki Pathshala - Learning Resources (open to all) */}
            <Route 
              path="paiso-ki-pathshala" 
              element={
                <ProtectedRoute allowedRoles={['client', 'advisor', 'manager', 'super_admin']}>
                  <PaisoKiPathshala />
                </ProtectedRoute>
              } 
            />
            {/* Child Future Ready Test (Client only — available to ALL plans incl. Free) */}
            <Route 
              path="child-future-ready" 
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <ChildFutureReady />
                </ProtectedRoute>
              } 
            />
            {/* Financial Health Check-Up (Client only — available to ALL plans incl. Free) */}
            <Route 
              path="financial-health-checkup" 
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <FinancialHealthCheckup />
                </ProtectedRoute>
              } 
            />
            {/* Family Vault - My Family Should Know (Client, Advisor, Manager) */}
            <Route 
              path="family-vault" 
              element={
                <ProtectedRoute allowedRoles={['client', 'advisor', 'manager']}>
                  <FamilyVault />
                </ProtectedRoute>
              } 
            />
            {/* Arth Gurukul Pro - Embedded Financial Planning Tool (Advisor, Manager) */}
            <Route 
              path="arth-kundli-pro" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <ArthKundliPro />
                </ProtectedRoute>
              } 
            />
            {/* Goal Manthan - Embedded Goal Planning Tool (Advisor, Manager) */}
            <Route 
              path="goal-manthan" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <GoalManthan />
                </ProtectedRoute>
              } 
            />
            {/* MSME Risk MRI - Embedded Risk Assessment Tool (Advisor, Manager) */}
            <Route 
              path="msme-risk-mri" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <MSMERiskMRI />
                </ProtectedRoute>
              } 
            />
            {/* Money Compass - Embedded Financial Direction Tool (Advisor, Manager) */}
            <Route 
              path="money-compass" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <MoneyCompass />
                </ProtectedRoute>
              } 
            />
            {/* LeadGen Engine (Advisor, Manager) */}
            <Route 
              path="leadgen-engine" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <LeadGenEngine />
                </ProtectedRoute>
              } 
            />
            {/* Debt Detox 360 (Advisor, Manager) */}
            <Route 
              path="debt-detox-360" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <DebtDetox360 />
                </ProtectedRoute>
              } 
            />
            {/* My Viral Brand (Advisor, Manager) */}
            <Route 
              path="my-viral-brand" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <MyViralBrand />
                </ProtectedRoute>
              } 
            />
            {/* Arth Gyan (Advisor, Manager) */}
            <Route 
              path="arth-gyan" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <ArthGyan />
                </ProtectedRoute>
              } 
            />
            {/* Sales Sutra (Advisor, Manager) */}
            <Route 
              path="sales-sutra" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <SalesSutra />
                </ProtectedRoute>
              } 
            />
            {/* Winning Ads (Advisor, Manager) */}
            <Route 
              path="winning-ads" 
              element={
                <ProtectedRoute allowedRoles={['advisor', 'manager']}>
                  <WinningAds />
                </ProtectedRoute>
              } 
            />
          </Route>

          {/* Default Redirect */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
